import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Users, ArrowRight, AlertTriangle, Clock, CheckCircle2 } from "lucide-react";
import { Bar, Doughnut } from "react-chartjs-2";
import {
    Chart as ChartJS,
    ArcElement,
    BarElement,
    CategoryScale,
    LinearScale,
    Legend,
    Tooltip,
} from "chart.js";

import { Button } from "../ui/button";
import StatusPill from "./StatusPill";
import { cn } from "../../utils/tailwindUtils";

ChartJS.register(ArcElement, BarElement, CategoryScale, LinearScale, Legend, Tooltip);

const STATUS_BUCKETS = [
    { key: "pending_employee_submission", label: "Pending Submission", color: "rgba(245,158,11,0.75)" },
    { key: "in_progress", label: "In Progress", color: "rgba(59,130,246,0.75)" },
    { key: "submitted_to_rm", label: "Submitted to Me", color: "rgba(99,102,241,0.85)" },
    { key: "sent_back_by_rm", label: "Sent Back", color: "rgba(249,115,22,0.75)" },
    { key: "pending_hr_approval", label: "With HR", color: "rgba(139,92,246,0.75)" },
    { key: "published", label: "Published", color: "rgba(16,185,129,0.85)" },
    { key: "overdue", label: "Overdue", color: "rgba(239,68,68,0.85)" },
];

const finalRatingFor = (g) => {
    if (g.hr_override_rating !== null && g.hr_override_rating !== undefined) return g.hr_override_rating;
    if (g.rm_rating !== null && g.rm_rating !== undefined) return g.rm_rating;
    if (g.self_rating !== null && g.self_rating !== undefined) return g.self_rating;
    return null;
};

const weightedScore = (goals = []) => {
    let totalW = 0;
    let sum = 0;
    for (const g of goals) {
        const r = finalRatingFor(g);
        if (r === null) continue;
        totalW += g.weightage || 0;
        sum += r * (g.weightage || 0);
    }
    return totalW ? Number((sum / totalW).toFixed(2)) : null;
};

const SmallStat = ({ icon: Icon, label, value, accent }) => (
    <div className={cn(
        "flex items-center gap-2 rounded-md border p-2.5 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700",
    )}>
        <div className={cn("p-1.5 rounded-md flex-shrink-0", accent)}>
            <Icon className="w-4 h-4" />
        </div>
        <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</p>
            <p className="text-base font-semibold text-gray-900 dark:text-gray-100 leading-tight">{value}</p>
        </div>
    </div>
);

const RMTeamSummary = ({ appraisals = [] }) => {
    const navigate = useNavigate();

    const counts = useMemo(() => {
        const c = Object.fromEntries(STATUS_BUCKETS.map((b) => [b.key, 0]));
        for (const a of appraisals) {
            if (c[a.status] !== undefined) c[a.status] += 1;
        }
        return c;
    }, [appraisals]);

    const totalTeam = appraisals.length;
    const pendingMine = counts.submitted_to_rm + counts.sent_back_by_rm;
    const inFlight = counts.pending_employee_submission + counts.in_progress;
    const completed = counts.published;

    // Doughnut — bucket counts, dropping zero buckets so the legend stays terse.
    const donutData = useMemo(() => {
        const present = STATUS_BUCKETS.filter((b) => counts[b.key] > 0);
        return {
            labels: present.map((b) => b.label),
            datasets: [{
                data: present.map((b) => counts[b.key]),
                backgroundColor: present.map((b) => b.color),
                borderWidth: 1,
            }],
        };
    }, [counts]);

    const donutOptions = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        cutout: "60%",
        plugins: {
            legend: { position: "bottom", labels: { boxWidth: 10, font: { size: 11 } } },
            tooltip: {
                callbacks: { label: (ctx) => ` ${ctx.label}: ${ctx.parsed}` },
            },
        },
    }), []);

    // Bar of final scores for team members whose appraisal is published.
    const scoredMembers = useMemo(() => {
        return appraisals
            .filter((a) => a.status === "published" || a.status === "hr_approved" || a.final_score !== null)
            .map((a) => ({
                id: a.id,
                name: a.employee?.name || "—",
                score: a.final_score !== null && a.final_score !== undefined ? Number(a.final_score) : weightedScore(a.goals),
            }))
            .filter((m) => m.score !== null);
    }, [appraisals]);

    const barData = useMemo(() => ({
        labels: scoredMembers.map((m) => m.name),
        datasets: [{
            label: "Final Score",
            data: scoredMembers.map((m) => m.score),
            backgroundColor: "rgba(16,185,129,0.7)",
            borderColor: "rgba(16,185,129,1)",
            borderWidth: 1,
            borderRadius: 4,
        }],
    }), [scoredMembers]);

    const barOptions = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        indexAxis: "y",
        plugins: { legend: { display: false }, tooltip: { mode: "index" } },
        scales: {
            x: { beginAtZero: true, max: 5, ticks: { stepSize: 1, font: { size: 11 } }, grid: { color: "rgba(148,163,184,0.2)" } },
            y: { ticks: { font: { size: 11 } }, grid: { display: false } },
        },
    }), []);

    const pendingReviewList = useMemo(() => {
        return appraisals
            .filter((a) => a.status === "submitted_to_rm")
            .slice(0, 5);
    }, [appraisals]);

    return (
        <div className="space-y-4">
            {/* Header card */}
            <div className="rounded-lg border border-indigo-200 dark:border-indigo-900/40 bg-gradient-to-r from-indigo-50 to-white dark:from-indigo-900/15 dark:to-gray-900 p-4 sm:p-5">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="flex items-center gap-3">
                        <div className="p-2 rounded-md bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300">
                            <Users className="w-5 h-5" />
                        </div>
                        <div>
                            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Team Performance Overview</h3>
                            <p className="text-xs text-muted-foreground">Appraisals across your direct reports</p>
                        </div>
                    </div>
                    <Button
                        size="sm"
                        className="bg-sidebar hover:bg-sidebar-primary text-white"
                        onClick={() => navigate("/appraisal/team-review")}
                    >
                        Open Team Review <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-4">
                    <SmallStat icon={Users} label="Team Size" value={totalTeam} accent="bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300" />
                    <SmallStat icon={AlertTriangle} label="Pending Review" value={pendingMine} accent="bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300" />
                    <SmallStat icon={Clock} label="In Flight" value={inFlight} accent="bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300" />
                    <SmallStat icon={CheckCircle2} label="Published" value={completed} accent="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300" />
                </div>
            </div>

            {/* Charts + pending list */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 sm:p-5">
                    <h3 className="text-base font-semibold mb-2 text-gray-900 dark:text-gray-100">Status Distribution</h3>
                    {totalTeam === 0 ? (
                        <div className="text-sm text-muted-foreground py-8 text-center">No team appraisals yet.</div>
                    ) : (
                        <div className="h-64">
                            <Doughnut data={donutData} options={donutOptions} />
                        </div>
                    )}
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 sm:p-5">
                    <h3 className="text-base font-semibold mb-2 text-gray-900 dark:text-gray-100">Final Scores</h3>
                    {scoredMembers.length === 0 ? (
                        <div className="text-sm text-muted-foreground py-8 text-center">No published appraisals yet.</div>
                    ) : (
                        <div className="h-64">
                            <Bar data={barData} options={barOptions} />
                        </div>
                    )}
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 sm:p-5">
                    <div className="flex items-center justify-between mb-2">
                        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Pending My Review</h3>
                        {pendingReviewList.length > 0 && (
                            <Button variant="ghost" size="sm" onClick={() => navigate("/appraisal/team-review")}>
                                View all <ArrowRight className="w-4 h-4 ml-1" />
                            </Button>
                        )}
                    </div>
                    {pendingReviewList.length === 0 ? (
                        <div className="text-sm text-muted-foreground py-8 text-center">
                            <CheckCircle2 className="w-7 h-7 mx-auto mb-2 text-emerald-400" />
                            All caught up.
                        </div>
                    ) : (
                        <ul className="divide-y divide-gray-100 dark:divide-gray-700">
                            {pendingReviewList.map((a) => (
                                <li
                                    key={a.id}
                                    onClick={() => navigate(`/appraisal/team-review/${a.id}`)}
                                    className="flex items-center justify-between gap-2 py-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/40 px-2 -mx-2 rounded-md transition-colors"
                                >
                                    <div className="min-w-0">
                                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                                            {a.employee?.name || "Employee"}
                                        </p>
                                        <p className="text-[11px] text-muted-foreground truncate">
                                            {a.employee?.designation}
                                        </p>
                                    </div>
                                    <StatusPill value={a.status} />
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        </div>
    );
};

export default RMTeamSummary;
